import styled from "@emotion/styled";
import type { NFT } from "@stepfinance/nft";
import { BaseButton, ContextButton } from "@stepfinance/react";
import { Col, Row } from "antd";
import { useEffect, useMemo, useState } from "react";

import { ReactComponent as SendIcon } from "../../../assets/mail.svg";
import { useAccountsContextRefresher } from "../../../contexts/accounts";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useNavigationContext } from "../../../contexts/navigation";
import { useNFTContext } from "../../../contexts/nft";
import { isCompressedNFT, isNFTWithTokenAccount } from "../../../models/nfts/nfts";
import { fetchAndLoadNFTOffChainData } from "../../../utils/nft/load";
import { formatStringWithPlaceholders } from "../../../utils/utils";
import { BurnModal } from "../../BurnModal/BurnModal";
import { Modal } from "../../Modal/Modal";
import { SendNFTContent } from "../../Send/SendNFTContent";
import { NavigationLink } from "../../shared";
import { NFTFileDisplay } from "../NFTFileDisplay";
import { TitleRow } from "../NFTGallery";
import { NFTAttributes } from "./NFTAttributes";

interface NFTDetailViewProps {
  focusedNFT: NFT;
  onImageClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, nft: NFT) => void;
}

export const NFTDetailView: React.FC<NFTDetailViewProps> = ({ focusedNFT, onImageClick }) => {
  const refreshTokenAccounts = useAccountsContextRefresher();
  const { setUserNFTs } = useNFTContext();
  const { wallet, watching } = useEnrichedWallet();

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [burnModalOpen, setBurnModalOpen] = useState(false);

  const { navigate } = useNavigationContext();
  const {
    currentLanguageData: {
      pages: {
        nftGallery,
        dashboard: {
          modules: {
            balances: { sendButton },
          },
        },
      },
    },
  } = useLanguageContext();

  // fetch NFT offchain data on component mount.
  useEffect(() => {
    fetchAndLoadNFTOffChainData([focusedNFT], setUserNFTs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareOnTwitterLink = useMemo(() => {
    if (wallet && wallet.adapter.publicKey && focusedNFT) {
      const nftTwitterMessage = formatStringWithPlaceholders(nftGallery.twitterMessage, focusedNFT.name);
      const currentURL =
        window.location.origin + window.location.pathname + `?watching=${wallet.adapter.publicKey.toBase58()}`;
      const tweetContent: string = encodeURIComponent([nftTwitterMessage, currentURL].join(""));
      return `https://twitter.com/intent/tweet?text=${tweetContent}`;
    }
  }, [wallet, focusedNFT, nftGallery.twitterMessage]);

  const isSendOrBurnableAsset = isNFTWithTokenAccount(focusedNFT) || isCompressedNFT(focusedNFT);

  return (
    <>
      <NFTDetailViewContainer>
        <TitleRow>
          <h2 className="nft-name">{focusedNFT.name}</h2>
        </TitleRow>
        <SelectAndTwitterShareRow>
          <NavigationLink onClick={() => navigate("/nft")}>{`< ${nftGallery.backToGallery}`}</NavigationLink>
          <a className="twitter-share-button" href={shareOnTwitterLink} target="_blank" rel="noreferrer">
            <ContextButton>{nftGallery.shareOnTwitter}</ContextButton>
          </a>
        </SelectAndTwitterShareRow>
        <Row
          className="nft-gallery-row"
          gutter={[16, 16]}
          style={{
            paddingTop: "20px",
            rowGap: "24px",
            width: "100%",
          }}
        >
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <Row>
              <div className="image-container">
                <NFTFileDisplay nft={focusedNFT} onImageClick={onImageClick} />
              </div>
            </Row>
            <Row>
              {!watching && (
                <div className="actions-container">
                  <BaseButton
                    className="action-button"
                    onClick={() => setSendModalOpen(true)}
                    disabled={!isSendOrBurnableAsset}
                  >
                    {sendButton}
                  </BaseButton>
                  <BaseButton
                    className="action-button"
                    onClick={() => setBurnModalOpen(true)}
                    disabled={!isSendOrBurnableAsset}
                  >
                    {nftGallery.burn}
                  </BaseButton>
                </div>
              )}
              {isSendOrBurnableAsset && (
                <>
                  <Modal
                    TitleIcon={SendIcon}
                    title={sendButton}
                    width={500}
                    onClose={() => setSendModalOpen(false)}
                    visible={sendModalOpen}
                  >
                    <SendNFTContent nft={focusedNFT} closeModal={() => setSendModalOpen(false)} />
                  </Modal>
                  <BurnModal
                    onSuccess={() => {
                      navigate("/nft");
                      setUserNFTs((currentState) => {
                        const nftMap = new Map(currentState);
                        nftMap.delete(focusedNFT.address.toBase58());
                        return nftMap;
                      });
                      refreshTokenAccounts();
                    }}
                    onClose={() => setBurnModalOpen(false)}
                    data={focusedNFT}
                    width={500}
                    visible={burnModalOpen}
                  />
                </>
              )}
            </Row>
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <NFTAttributes nft={focusedNFT} />
          </Col>
        </Row>
      </NFTDetailViewContainer>
    </>
  );
};

const NFTDetailViewContainer = styled.div`
.actions-container {
  display: flex;
  width: 100%;
  margin-top: 20px;
  .action-button {
    height: 60px;
    width: 100%;
    border-radius: 8px;
    margin-right: 16px;
    &:last-of-type {
      margin-right: 0px;
    }
  }
`;

const SelectAndTwitterShareRow = styled.div`
  display: flex;
  justify-content: space-between;
  > * {
    margin: auto 0;
  }
`;
